import { useState, useCallback, useMemo, useEffect } from 'react';
import type { Service, TimeSlot, BookingStatus, BookingSummary } from '@/types';

const FRIENDLY_CAPACITY_MESSAGE =
  'Booking with this business is temporarily restricted due to high demand. Please try again later.';

function mapBookingErrorMessage(message?: string, code?: string): string {
  if (code === 'PROVIDER_CAPACITY_RESTRICTED' || code === 'BOOKING_DISABLED') {
    return FRIENDLY_CAPACITY_MESSAGE;
  }
  if (message?.toLowerCase().includes('quota exceeded')) {
    return FRIENDLY_CAPACITY_MESSAGE;
  }
  return message || 'Something went wrong. Please try again.';
}
import { getServicePriceForCurrency } from '@/utils/currency';
import * as bookingApi from '@/services/bookingApi';
import { teamApi } from '@/services/teamApi';
import {
  buildWizardStepOrder,
  getNextStep,
  getPrevStep,
  type WizardStepId,
  type BookingEntryMode,
} from '@/components/booking/bookingWizardSteps';
import type {
  BookableMaterial,
  MaterialSelection,
} from '@/components/booking/MaterialsSelection';
import type { BookingLocationOption } from '@/components/booking/LocationPicker';

interface UseBookingWizardOptions {
  entryMode?: BookingEntryMode;
  profileId: string;
  username: string;
  initialServices?: Service[];
  defaultCurrency?: string;
  supportedCurrencies?: string[];
  verificationMethod?: 'email' | 'phone';
  clientVerificationEnabled?: boolean;
  fixedStaffId?: string | null;
  locationOptions?: BookingLocationOption[];
  variant?: 'page' | 'modal' | 'embed';
  allowCustomerStaffSelection?: boolean;
}

export function useBookingWizard(options: UseBookingWizardOptions) {
  const {
    entryMode = 'location',
    profileId: initialProfileId,
    username: initialUsername,
    initialServices = [],
    defaultCurrency = 'EUR',
    supportedCurrencies = [],
    verificationMethod = 'email',
    clientVerificationEnabled = true,
    fixedStaffId = null,
    locationOptions = [],
    allowCustomerStaffSelection = true,
  } = options;

  const [currentStep, setCurrentStep] = useState<WizardStepId>(
    entryMode === 'staff' && locationOptions.length > 1 ? 'location' : 'services'
  );
  const [profileId, setProfileId] = useState(initialProfileId);
  const [username, setUsername] = useState(initialUsername);
  const [selectedServices, setSelectedServices] = useState<Service[]>(initialServices);
  const [assignedUserId, setAssignedUserId] = useState<string | null>(fixedStaffId);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: '',
    notes: '',
  });
  const [bookingResult, setBookingResult] = useState<BookingSummary | null>(null);
  const [selectedCurrency, setSelectedCurrency] = useState<string | null>(defaultCurrency);
  const [imagePaths, setImagePaths] = useState<string[]>([]);
  const [sessionId] = useState(() => bookingApi.getBookingSessionId());
  const [bookableMaterials, setBookableMaterials] = useState<BookableMaterial[]>([]);
  const [materialSelections, setMaterialSelections] = useState<MaterialSelection[]>([]);
  const [capableStaff, setCapableStaff] = useState<{ id: string; full_name: string }[]>([]);

  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [pollingCount, setPollingCount] = useState(0);
  const [bookingToken, setBookingTokenState] = useState<string | null>(null);

  const currency = selectedCurrency || defaultCurrency;
  const currencies = supportedCurrencies.length > 0 ? supportedCurrencies : [defaultCurrency];
  const requiresEmail = clientVerificationEnabled && verificationMethod === 'email';
  const requiresPhone = true;

  const locationFixed = entryMode === 'location';
  const staffFixed = entryMode === 'staff' && !!fixedStaffId;

  const stepOrder = useMemo(
    () =>
      buildWizardStepOrder({
        entryMode,
        locationOptionsCount: locationOptions.length,
        locationFixed,
        staffFixed,
        hasBookableMaterials: bookableMaterials.length > 0,
        capableStaffCount: capableStaff.length,
        allowCustomerStaffSelection,
      }),
    [
      entryMode,
      locationOptions.length,
      locationFixed,
      staffFixed,
      bookableMaterials.length,
      capableStaff.length,
      allowCustomerStaffSelection,
    ]
  );

  useEffect(() => {
    if (!stepOrder.includes(currentStep)) {
      setCurrentStep(stepOrder[0]);
    }
  }, [stepOrder, currentStep]);

  const effectiveAssignedUserId = useMemo(() => {
    if (!allowCustomerStaffSelection && !staffFixed) return null;
    if (assignedUserId) return assignedUserId;
    if (capableStaff.length === 1) return capableStaff[0].id;
    return null;
  }, [assignedUserId, capableStaff, allowCustomerStaffSelection, staffFixed]);

  const loadCapableStaff = useCallback(async () => {
    if (staffFixed || selectedServices.length === 0) return;
    try {
      const serviceIds = selectedServices.map((s) => s.id);
      const staff = await teamApi.getPublicStaff(username, serviceIds);
      setCapableStaff(
        staff.map((s) => ({ id: s.id, full_name: s.full_name || s.username || 'Staff' }))
      );
      if (staff.length === 1) {
        setAssignedUserId(staff[0].id);
      } else if (!allowCustomerStaffSelection) {
        setAssignedUserId(null);
      }
    } catch {
      setCapableStaff([]);
    }
  }, [username, selectedServices, staffFixed, allowCustomerStaffSelection]);

  const loadMaterials = useCallback(async () => {
    if (selectedServices.length === 0) {
      setBookableMaterials([]);
      setMaterialSelections([]);
      return;
    }
    try {
      const materials = await bookingApi.getServiceMaterials(
        username,
        selectedServices.map((s) => s.id)
      );
      setBookableMaterials(materials);
      const required = materials
        .filter((m) => m.is_required)
        .map((m) => ({
          material_id: m.id,
          quantity: m.default_quantity || 1,
        }));
      setMaterialSelections(required);
    } catch {
      setBookableMaterials([]);
    }
  }, [username, selectedServices]);

  useEffect(() => {
    if (selectedServices.length > 0) {
      loadCapableStaff();
      loadMaterials();
    }
  }, [selectedServices, loadCapableStaff, loadMaterials]);

  const selectLocation = useCallback(
    (locationId: string) => {
      const loc = locationOptions.find((l) => l.id === locationId);
      if (loc) {
        setProfileId(loc.id);
        setUsername(loc.slug);
        setSelectedServices([]);
        setSelectedDate(null);
        setSelectedSlot(null);
        setAssignedUserId(fixedStaffId);
      }
    },
    [locationOptions, fixedStaffId]
  );

  const selectServices = useCallback((services: Service[]) => {
    setSelectedServices(services);
    setSelectedSlot(null);
    setAvailableSlots([]);
    if (!staffFixed) setAssignedUserId(fixedStaffId);
  }, [staffFixed, fixedStaffId]);

  const selectAssignedUser = useCallback((userId: string | null) => {
    setAssignedUserId(userId);
    setSelectedSlot(null);
    setAvailableSlots([]);
  }, []);

  const selectDate = useCallback((date: string) => {
    setSelectedDate(date);
    setSelectedSlot(null);
    setAvailableSlots([]);
  }, []);

  const selectSlot = useCallback((slot: string) => {
    setSelectedSlot(slot);
  }, []);

  const selectCurrency = useCallback((c: string) => {
    setSelectedCurrency(c);
  }, []);

  const updateCustomerInfo = useCallback(
    (info: Partial<typeof customerInfo>) => {
      setCustomerInfo((prev) => ({ ...prev, ...info }));
    },
    []
  );

  const addImagePath = useCallback((path: string) => {
    setImagePaths((prev) => (prev.length >= 3 ? prev : [...prev, path]));
  }, []);

  const removeImagePath = useCallback((path: string) => {
    setImagePaths((prev) => prev.filter((p) => p !== path));
  }, []);

  const fetchAvailableSlots = useCallback(async () => {
    if (!selectedDate || selectedServices.length === 0) return;
    setIsLoading(true);
    setError(null);
    try {
      const availability = await bookingApi.getAvailableSlots(
        username,
        selectedDate,
        selectedServices.map((s) => s.id),
        effectiveAssignedUserId || undefined
      );
      setAvailableSlots(availability.slots);
    } catch (err: unknown) {
      setError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          'Failed to load available slots'
      );
      setAvailableSlots([]);
    } finally {
      setIsLoading(false);
    }
  }, [username, selectedDate, selectedServices, effectiveAssignedUserId]);

  useEffect(() => {
    if (selectedDate && selectedServices.length > 0 && currentStep === 'time') {
      fetchAvailableSlots();
    }
  }, [selectedDate, selectedServices, effectiveAssignedUserId, currentStep, fetchAvailableSlots]);

  const lockSlot = useCallback(async (): Promise<boolean> => {
    if (!selectedDate || !selectedSlot) {
      setError('Please select a date and time slot');
      return false;
    }
    setIsLoading(true);
    setError(null);
    try {
      const result = await bookingApi.lockSlot(
        profileId,
        selectedDate,
        selectedSlot,
        effectiveAssignedUserId || undefined
      );
      if (!result.success) {
        setError(mapBookingErrorMessage(result.message, (result as { code?: string }).code));
        return false;
      }
      return true;
    } catch (err: unknown) {
      const e = err as { response?: { status?: number; data?: { message?: string; code?: string; retry_after?: number } } };
      if (e.response?.status === 429 && e.response.data?.retry_after) {
        const minutes = Math.ceil(e.response.data.retry_after / 60);
        setError(`Too many booking attempts. Please try again in ${minutes} minute(s).`);
      } else {
        setError(mapBookingErrorMessage(e.response?.data?.message, e.response?.data?.code));
      }
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [profileId, selectedDate, selectedSlot, effectiveAssignedUserId]);

  const submitBooking = useCallback(async () => {
    if (selectedServices.length === 0) return { success: false, error: 'Select at least one service' };
    if (!selectedDate || !selectedSlot) return { success: false, error: 'Select date and time' };
    if (!customerInfo.name.trim()) return { success: false, error: 'Enter your name' };
    if (!customerInfo.phone.trim()) return { success: false, error: 'Enter your phone number' };
    if (requiresEmail && !customerInfo.email.trim()) {
      return { success: false, error: 'Enter your email for verification' };
    }

    setIsLoading(true);
    setError(null);
    try {
      const result = await bookingApi.createBooking({
        profile_id: profileId,
        service_ids: selectedServices.map((s) => s.id),
        customer_name: customerInfo.name,
        customer_email: customerInfo.email || undefined,
        customer_phone: customerInfo.phone,
        customer_notes: customerInfo.notes || undefined,
        booking_date: selectedDate,
        booking_time: selectedSlot,
        image_paths: imagePaths.length > 0 ? imagePaths : undefined,
        currency,
        assigned_user_id: effectiveAssignedUserId || undefined,
        materials: materialSelections.length > 0 ? materialSelections : undefined,
      });

      setBookingResult({
        id: result.booking_id,
        status: result.status as BookingStatus,
        expires_at: result.expires_at || '',
        verification_method: result.verification_method,
        customer_email: customerInfo.email,
        customer_name: customerInfo.name,
        customer_phone: customerInfo.phone,
        currency,
      });
      setBookingTokenState(result.booking_id);

      return {
        success: true,
        bookingId: result.booking_id,
        verificationMethod: result.verification_method,
        clientVerificationEnabled: result.client_verification_enabled,
      };
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string; code?: string } } };
      const msg = mapBookingErrorMessage(e.response?.data?.message, e.response?.data?.code);
      setError(msg);
      return { success: false, error: msg };
    } finally {
      setIsLoading(false);
    }
  }, [
    selectedServices,
    selectedDate,
    selectedSlot,
    customerInfo,
    requiresEmail,
    profileId,
    imagePaths,
    currency,
    effectiveAssignedUserId,
    materialSelections,
  ]);

  const pollConfirmationStatus = useCallback(
    async (token?: string) => {
      const tokenToUse = token || bookingToken;
      if (!tokenToUse) return { success: false, error: 'No booking token' };
      setIsPolling(true);
      setPollingCount(0);
      try {
        const result = await bookingApi.pollBookingStatus(tokenToUse);
        setBookingResult((prev) => ({
          id: result.booking.id,
          status: result.status as BookingStatus,
          expires_at: prev?.expires_at || '',
          verification_method: prev?.verification_method,
          booking_datetime: result.booking.booking_datetime,
          booking_date: result.booking.booking_date,
          booking_time: result.booking.booking_time,
          duration_minutes: result.booking.duration_minutes,
          total_price: result.booking.total_price,
          services: result.booking.services,
          customer_email: result.booking.customer_email,
          customer_name: result.booking.customer_name,
          currency: prev?.currency,
        }));
        setPollingCount(1);
        return { success: true };
      } catch (err: unknown) {
        const msg =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          'Failed to poll status';
        setError(msg);
        return { success: false, error: msg };
      } finally {
        setIsPolling(false);
      }
    },
    [bookingToken]
  );

  const totalDuration = selectedServices.reduce(
    (sum, s) => sum + (s.duration_minutes || 30),
    0
  );

  const serviceTotal = selectedServices.reduce((sum, service) => {
    return sum + (getServicePriceForCurrency(service, currency) || 0);
  }, 0);

  const selectedSlotMeta = availableSlots.find((s) => s.time === selectedSlot);
  const priceMultiplier = selectedSlotMeta?.price_multiplier ?? 1;
  const materialsTotal = materialSelections.reduce((sum, sel) => {
    const mat = bookableMaterials.find((m) => m.id === sel.material_id);
    if (!mat || mat.included_in_price) return sum;
    const variant = mat.variants.find((v) => v.id === sel.material_variant_id);
    const cents = variant?.effective_price ?? mat.base_price;
    return sum + (cents / 100) * (sel.quantity || 1);
  }, 0);

  const totalPrice = Math.round((serviceTotal * priceMultiplier + materialsTotal) * 100) / 100;

  const goNext = useCallback(async () => {
    setError(null);
    if (currentStep === 'time') {
      const locked = await lockSlot();
      if (!locked) return;
    }
    if (currentStep === 'details') {
      const result = await submitBooking();
      if (!result.success) return;
      setCurrentStep('done');
      if (
        result.clientVerificationEnabled &&
        result.verificationMethod === 'email'
      ) {
        await pollConfirmationStatus(result.bookingId);
      }
      return;
    }
    const next = getNextStep(currentStep, stepOrder);
    if (next) setCurrentStep(next);
  }, [currentStep, stepOrder, lockSlot, submitBooking, pollConfirmationStatus]);

  const goBack = useCallback(() => {
    const prev = getPrevStep(currentStep, stepOrder);
    if (prev) setCurrentStep(prev);
  }, [currentStep, stepOrder]);

  const canProceed = useMemo(() => {
    switch (currentStep) {
      case 'location':
        return !!profileId && locationOptions.some((l) => l.id === profileId);
      case 'services':
        return selectedServices.length > 0;
      case 'materials':
        return bookableMaterials
          .filter((m) => m.is_required)
          .every((m) => materialSelections.some((s) => s.material_id === m.id));
      case 'staff':
        return true;
      case 'date':
        return !!selectedDate;
      case 'time':
        return !!selectedSlot;
      case 'details':
        return (
          !!customerInfo.name.trim() &&
          !!customerInfo.phone.trim() &&
          (!requiresEmail || !!customerInfo.email.trim())
        );
      default:
        return false;
    }
  }, [
    currentStep,
    profileId,
    locationOptions,
    selectedServices,
    bookableMaterials,
    materialSelections,
    effectiveAssignedUserId,
    selectedDate,
    selectedSlot,
    customerInfo,
    requiresEmail,
  ]);

  const reset = useCallback(() => {
    setCurrentStep(stepOrder[0]);
    setSelectedServices([]);
    setAssignedUserId(fixedStaffId);
    setSelectedDate(null);
    setSelectedSlot(null);
    setCustomerInfo({ name: '', email: '', phone: '', notes: '' });
    setBookingResult(null);
    setImagePaths([]);
    setMaterialSelections([]);
    setBookableMaterials([]);
    setCapableStaff([]);
    setError(null);
    setBookingTokenState(null);
  }, [stepOrder, fixedStaffId]);

  return {
    currentStep,
    stepOrder,
    profileId,
    username,
    selectedServices,
    assignedUserId: effectiveAssignedUserId,
    selectedDate,
    selectedSlot,
    customerInfo,
    bookingResult,
    imagePaths,
    sessionId,
    bookableMaterials,
    materialSelections,
    setMaterialSelections,
    capableStaff,
    availableSlots,
    isLoading,
    error,
    isPolling,
    pollingCount,
    selectLocation,
    selectServices,
    selectAssignedUser,
    selectDate,
    selectSlot,
    selectCurrency,
    updateCustomerInfo,
    addImagePath,
    removeImagePath,
    fetchAvailableSlots,
    goNext,
    goBack,
    canProceed,
    totalDuration,
    totalPrice,
    selectedCurrency: currency,
    supportedCurrencies: currencies,
    requiresPhone,
    requiresEmail,
    clientVerificationEnabled,
    reset,
    setCurrentStep,
    pollConfirmationStatus,
  };
}
